/**
 * Eventos de negocio hacia GA4, a través del `dataLayer` de Google Tag Manager.
 *
 * ── Por qué existe este fichero ────────────────────────────────────────────────────────────────
 * Hasta ahora el sitio solo cargaba el contenedor de GTM (`GTM-NL6VQJL`, en `src/app/layout.tsx`)
 * y no empujaba NADA al `dataLayer`. GA4 recibía únicamente la medición automática (`page_view`,
 * `session_start`, `first_visit`, `user_engagement`) más un evento `clic` que un tag del propio
 * contenedor dispara con los clics en enlaces. Resultado: inicios de inscripción, inscripciones
 * completadas, conversión orgánica e ingresos salían a cero — se medía tráfico, no negocio.
 *
 * ── IMPORTANTE: esto es la mitad del trabajo ───────────────────────────────────────────────────
 * Un `dataLayer.push` NO llega a GA4 por sí solo. En el contenedor de GTM hace falta, por cada
 * evento de aquí, un disparador de tipo "evento personalizado" y un tag "Evento de GA4" que lo
 * reenvíe. Mientras eso no exista, estos eventos se ven en la vista previa de GTM pero no en GA4.
 *
 * ── Relación con Facebook CAPI ─────────────────────────────────────────────────────────────────
 * Los cuatro puntos de disparo son los mismos que ya usaba el embudo de Facebook CAPI, porque la
 * parte difícil —decidir CUÁNDO una acción cuenta como conversión— ya estaba resuelta y razonada
 * ahí (presencial fuera, pago pendiente fuera, cobro diferido de lista de espera fuera). Lo que NO
 * se comparte es el consentimiento: CAPI vive en la categoría `advertisement` de CookieYes y GA4
 * en `analytics`, que son decisiones distintas del usuario. Por eso cada llamada tiene su propio
 * gate y ninguna cuelga de la otra: quien acepta analítica y rechaza publicidad debe contar en GA4.
 */

import { hasAnalyticsConsent } from '@/utils/cookieConsent';
import { normalizeCurrencyCode } from '@/utils/locale';
import type { ReservationSessionItem } from '@/types/reservation';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** Artículo en el formato de ecommerce de GA4. */
export interface Ga4Item {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

/**
 * Empuja un evento al `dataLayer`. Silencioso por diseño: la analítica nunca debe romper ni
 * retrasar el flujo del usuario.
 *
 * El `ecommerce: null` previo es la limpieza que recomienda Google: el `dataLayer` es acumulativo
 * y, sin él, un `purchase` heredaría los `items` del `view_item` anterior de la misma navegación.
 */
function pushEvent(event: string, params: Record<string, unknown> = {}, ecommerce?: Record<string, unknown>): boolean {
  if (typeof window === 'undefined') return false;

  // El try envuelve TAMBIÉN la lectura del consentimiento a propósito. Estas funciones se llaman
  // desde el manejador del botón de inscripción y desde el justificante: si algo aquí lanzara
  // —una extensión que redefine `dataLayer` con un proxy hostil, `document.cookie` inaccesible en
  // un iframe sandboxeado—, la excepción subiría por el manejador del clic y dejaría al usuario
  // sin poder inscribirse. Ninguna métrica justifica eso.
  try {
    if (!hasAnalyticsConsent()) return false;
    window.dataLayer = window.dataLayer || [];
    if (ecommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...params, ...(ecommerce ? { ecommerce } : {}) });
    return true;
  } catch {
    // Silencio deliberado: la analítica nunca puede romper ni retrasar el flujo del usuario.
    return false;
  }
}

/**
 * Precio que se anuncia en la portada/ficha: la tarifa visible más barata con precio. Excluye las
 * tarifas ocultas al público y las de precio oculto (`hidePrice`) — mandar ese precio a GA4 (y al
 * contenedor del organizador, en el microsite) delataría lo que el organizador decidió no mostrar.
 */
export function lowestVisiblePrice(fees: { price?: number; hidePrice?: boolean; visible?: boolean }[]): number | undefined {
  const prices = fees
    .filter(f => f.visible !== false && !f.hidePrice && (f.price ?? 0) > 0)
    .map(f => f.price as number);
  return prices.length ? Math.min(...prices) : undefined;
}

/** Ficha de evento vista (ficha pública `/event/{alias}` o portada del microsite `/site/{alias}`). */
export function trackViewItem(params: {
  eventId?: number | string;
  eventName?: string;
  sport?: string;
  price?: number;
  currency?: string;
}): void {
  const currency = normalizeCurrencyCode(params.currency);
  pushEvent('view_item', {}, {
    currency,
    value: params.price ?? 0,
    items: [{
      item_id: params.eventId != null ? String(params.eventId) : undefined,
      item_name: params.eventName,
      item_category: params.sport,
      price: params.price,
      quantity: 1,
    }],
  });
}

/**
 * El usuario ha pulsado el botón de inscribirse.
 *
 * `registration_flow` distingue si la inscripción continúa en este sitio o se abre en el WebRTS
 * antiguo: mientras dure la migración, esos usuarios completan la compra en otro dominio y otro
 * contenedor de GTM, así que su `purchase` NO va a llegar aquí. Sin este parámetro, la tasa de
 * conversión mezcla dos poblaciones y parece mucho peor de lo que es.
 */
export function trackBeginCheckout(params: {
  eventId?: number | string;
  eventName?: string;
  sport?: string;
  price?: number;
  currency?: string;
  flow: 'new' | 'legacy';
}): boolean {
  const currency = normalizeCurrencyCode(params.currency);
  return pushEvent('begin_checkout', { registration_flow: params.flow }, {
    currency,
    value: params.price ?? 0,
    items: [{
      item_id: params.eventId != null ? String(params.eventId) : undefined,
      item_name: params.eventName,
      item_category: params.sport,
      price: params.price,
      quantity: 1,
    }],
  });
}

/** Búsqueda en el buscador de eventos. */
export function trackSearch(searchTerm: string): void {
  const term = (searchTerm ?? '').trim();
  if (!term) return;
  pushEvent('search', { search_term: term.toLowerCase() });
}

/** Clave de idempotencia del `purchase`: un refresco del justificante no debe volver a contarlo. */
const PURCHASE_TRACKED_PREFIX = 'ga4_purchase_';

/**
 * Compra completada.
 *
 * GA4 no deduplica por `transaction_id` — dos envíos con el mismo id son dos compras en los
 * informes —, y el justificante es una página que el usuario refresca, comparte y reabre desde el
 * historial. El guard vive en `localStorage` en vez de en un `useRef` precisamente por eso:
 * un ref solo cubre el montaje actual del componente.
 */
export function trackPurchase(params: {
  transactionId: string;
  value: number;
  currency?: string;
  items: Ga4Item[];
  /**
   * Identificador con el que se recuerda que esta compra ya se reportó. Por defecto el propio
   * `transactionId`, pero el justificante pasa el id de la RESERVA a propósito: el número de
   * inscripción lo sella Finalize de forma asíncrona, así que la primera visita puede no tenerlo
   * todavía y un refresco un segundo después sí. Dedupdando por el id de reserva —que no cambia—
   * esa segunda pasada no cuenta una compra nueva.
   */
  dedupeKey?: string;
}): void {
  if (typeof window === 'undefined') return;

  const key = `${PURCHASE_TRACKED_PREFIX}${params.dedupeKey ?? params.transactionId}`;
  try {
    if (localStorage.getItem(key)) return;
  } catch {
    // Sin localStorage (modo privado, cuota llena) se sigue adelante: es preferible arriesgar un
    // duplicado ocasional a perder la conversión, que es el dato que hoy falta por completo.
  }

  const pushed = pushEvent('purchase', {}, {
    transaction_id: params.transactionId,
    value: params.value,
    currency: normalizeCurrencyCode(params.currency),
    items: params.items,
  });

  // La marca se escribe solo si el evento SALIÓ. Marcarla antes tenía un fallo sutil: si el
  // usuario aún no había aceptado la analítica, el push no se hacía pero la compra quedaba
  // apuntada como enviada, y al aceptar las cookies y refrescar ya no se reportaba nunca.
  if (!pushed) return;
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Ídem: sin persistencia, el riesgo es un duplicado, no una pérdida.
  }
}

/**
 * Importe total cobrado por una reserva, a partir de su documento.
 *
 * ⚠️ PAREADO CON EL BACKEND: replica exactamente `ComputeReservationTotal` de
 * `Root/src/Controllers/FacebookCAPIController.cs` del repo BKD-Public-NET8, que es la fórmula con
 * la que se valora la compra para Facebook. Si una de las dos cambia, Meta y GA4 empiezan a
 * reportar ingresos distintos para la misma inscripción. Los tests de
 * `src/utils/__tests__/analytics.test.ts` fijan los casos.
 *
 * NO es el FE calculando un precio: las líneas de `economics` llegan del documento de la reserva
 * ya rehidratadas server-side (saneado de `fix_amounts`), así que esto solo suma lo que el backend
 * decidió cobrar. Por eso incluye comisiones (`platformCost` + `commissionCost`) y por eso las
 * líneas negativas —cupones de descuento— restan solas, sin tratamiento especial.
 */
export function sumReservationTotal(items: ReservationSessionItem[] | undefined | null): number {
  if (!items) return 0;
  let total = 0;
  for (const item of items) {
    for (const line of item.economics ?? []) {
      total += line.price * line.quantity + (line.platformCost ?? 0) + (line.commissionCost ?? 0);
    }
  }
  return total;
}

/** Líneas de la reserva en el formato de artículos de GA4 (una por inscrito). */
export function reservationItemsToGa4(items: ReservationSessionItem[] | undefined | null): Ga4Item[] {
  return (items ?? []).map(item => ({
    item_id: item.feeId,
    item_name: item.feeName,
    price: item.feePrice,
    quantity: 1,
  }));
}
